/**
 * Google Maps API Loader - Seawater Service Integration Pattern
 * Provides robust, cached loading of Google Maps JavaScript API
 * Follows Seawater patterns for external service integration with fallback
 */

import React from 'react';

export interface GoogleMapsConfig {
  apiKey?: string;
  libraries?: string[];
  version?: string;
  region?: string;
  language?: string;
}

export interface GoogleMapsLoadResult {
  success: boolean;
  maps?: typeof google.maps;
  error?: string;
  loadTime?: number;
}

class GoogleMapsAPILoader {
  private loadPromise: Promise<GoogleMapsLoadResult> | null = null;
  private isLoaded = false;
  private loadStartTime = 0;

  private defaultConfig: GoogleMapsConfig = {
    libraries: ['places', 'geometry'],
    version: 'weekly',
    region: 'US',
    language: 'en'
  };

  /**
   * Load Google Maps API following Seawater service integration patterns
   * @param config Configuration for Maps API loading
   * @returns Promise with load result
   */
  async loadGoogleMaps(config: GoogleMapsConfig = {}): Promise<GoogleMapsLoadResult> {
    // Return cached result if already loaded
    if (this.isLoaded && window.google?.maps) {
      return {
        success: true,
        maps: window.google.maps,
        loadTime: 0
      };
    }

    // Return existing load promise if loading in progress
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadStartTime = performance.now();
    this.loadPromise = this.performLoad(config);

    return this.loadPromise;
  }

  private async performLoad(config: GoogleMapsConfig): Promise<GoogleMapsLoadResult> {
    try {
      const finalConfig = { ...this.defaultConfig, ...config };

      // Check if API key is available (Seawater pattern: graceful degradation)
      const apiKey = finalConfig.apiKey || 
                    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
                    process.env.REACT_APP_GOOGLE_API_KEY;

      if (!apiKey) {
        console.warn('Google Maps API key not found. Location services will be limited.');
        return {
          success: false,
          error: 'API key not configured'
        };
      }

      // Check if already loaded by another instance
      if (window.google?.maps) {
        this.isLoaded = true;
        return {
          success: true,
          maps: window.google.maps,
          loadTime: performance.now() - this.loadStartTime
        };
      }

      // Build URL with parameters
      const params = new URLSearchParams({
        key: apiKey,
        libraries: finalConfig.libraries?.join(',') || '',
        v: finalConfig.version || 'weekly'
      });

      if (finalConfig.region) params.set('region', finalConfig.region);
      if (finalConfig.language) params.set('language', finalConfig.language);

      const scriptUrl = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;

      // Load the script
      await this.loadScript(scriptUrl);

      // Wait for Google Maps to be available
      await this.waitForGoogleMaps();

      const loadTime = performance.now() - this.loadStartTime;
      this.isLoaded = true;

      return {
        success: true,
        maps: window.google.maps,
        loadTime
      };

    } catch (error) {
      console.error('Failed to load Google Maps API:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    } finally {
      this.loadPromise = null;
    }
  }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.async = true;

      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

      document.head.appendChild(script);
    });
  }

  private waitForGoogleMaps(timeout = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      const checkGoogleMaps = () => {
        if (window.google?.maps) {
          resolve();
          return;
        }

        if (Date.now() - startTime > timeout) {
          reject(new Error('Timeout waiting for Google Maps to load'));
          return;
        }

        setTimeout(checkGoogleMaps, 100);
      };

      checkGoogleMaps();
    });
  }

  /**
   * Check if Google Maps API is currently loaded
   */
  isGoogleMapsLoaded(): boolean {
    return this.isLoaded && !!window.google?.maps;
  }

  /**
   * Get current Google Maps instance if loaded
   */
  getGoogleMaps(): typeof google.maps | null {
    return this.isGoogleMapsLoaded() ? window.google.maps : null;
  }

  /**
   * Reset loader state (useful for testing)
   */
  reset(): void {
    this.loadPromise = null;
    this.isLoaded = false;
    this.loadStartTime = 0;
  }
}

// Singleton instance
export const googleMapsLoader = new GoogleMapsAPILoader();

// Utility function for React components
export const useGoogleMaps = () => {
  const [loadResult, setLoadResult] = React.useState<GoogleMapsLoadResult | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const loadMaps = React.useCallback(async (config?: GoogleMapsConfig) => {
    if (googleMapsLoader.isGoogleMapsLoaded()) {
      setLoadResult({
        success: true,
        maps: googleMapsLoader.getGoogleMaps()!
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await googleMapsLoader.loadGoogleMaps(config);
      setLoadResult(result);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    loadMaps,
    isLoading,
    loadResult,
    isLoaded: googleMapsLoader.isGoogleMapsLoaded(),
    maps: googleMapsLoader.getGoogleMaps()
  };
};

// Global type declaration
declare global {
  interface Window {
    google: typeof google;
  }
}